import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, BookOpen, Award, CheckCircle2, Sparkles, GraduationCap, FlaskConical, ChevronDown } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DisclaimerBanner } from "../components/Layout";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1483137140003-ae073b395549?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxoZXJiYWxpc3QlMjBib3RhbmljYWwlMjBoZXJicyUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzkwNjg4OTQ5fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [overview, setOverview] = useState(null);
  const [openModule, setOpenModule] = useState(1);
  const [loadingEnroll, setLoadingEnroll] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/course/overview").then(({ data }) => setOverview(data)).catch(() => {});
  }, []);

  const enroll = async () => {
    if (!user) {
      navigate("/register");
      return;
    }
    if (user.access_active || user.role === "admin") {
      navigate("/dashboard");
      return;
    }
    setLoadingEnroll(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setLoadingEnroll(false);
    }
  };

  const chaptersByModule = (mid) =>
    (overview?.chapters || []).filter((c) => c.module_id === mid);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Botanical herbs" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(250,248,245,0.82) 0%, rgba(250,248,245,0.96) 100%)" }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 lg:py-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-mono text-[#2C4A3E] bg-[#E8ECE8] px-3 py-1.5 rounded-full">
              <Sparkles className="w-3.5 h-3.5" /> 90-Chapter Certification Program
            </span>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight text-[#1A2621]">
              Become a Confident <span className="text-[#2C4A3E] italic">Herbalism Practitioner</span>
            </h1>
            <p className="mt-6 text-lg font-light text-[#5A6B61] leading-relaxed">
              A warm, self-paced journey through the craft of plant medicine — ninety illustrated
              chapters, instant quizzes, and a final certification exam, all leading to your
              print-worthy practitioner certificate.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                data-testid="enroll-now-btn"
                onClick={enroll}
                disabled={loadingEnroll}
                className="px-8 py-4 rounded-full bg-[#2C4A3E] text-[#FAF8F5] font-semibold text-base hover:bg-[#223B31] transition-all hover:-translate-y-0.5 shadow-lg disabled:opacity-60"
              >
                {loadingEnroll ? "Preparing checkout…" : user?.access_active || user?.role === "admin" ? "Continue Learning" : `Enroll — $${overview?.price_usd ?? 99} one-time`}
              </button>
              <div className="flex items-center gap-2 text-sm text-[#5A6B61]">
                <CheckCircle2 className="w-4 h-4 text-[#3D7A5A]" /> One year of access · Certificate included
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Highlights */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {[
            { icon: BookOpen, title: "90 Chapters", desc: "Across 10 cohesive modules", slug: "chapters" },
            { icon: FlaskConical, title: "Instant Quizzes", desc: "Auto-graded, 85% to pass", slug: "quizzes" },
            { icon: GraduationCap, title: "Final Exam", desc: "35 questions, 80% to certify", slug: "exam" },
            { icon: Award, title: "Certificate", desc: "Personalized & printable", slug: "certificate" },
          ].map((f, i) => (
            <motion.div
              key={f.title}
              data-testid={`highlight-${f.slug}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-[#F3EFEA] border border-[#2C4A3E]/12 rounded-2xl p-6 hover:shadow-md transition-all"
            >
              <f.icon className="w-7 h-7 text-[#2C4A3E]" />
              <h3 className="mt-4 font-serif text-xl text-[#1A2621]">{f.title}</h3>
              <p className="text-sm text-[#5A6B61] mt-1">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Curriculum */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-widest font-mono text-[#829B88]">The Curriculum</span>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-[#1A2621]">Ten Modules · Ninety Chapters</h2>
          <p className="mt-3 text-[#5A6B61]">Chapters unlock in order as you progress through the material.</p>
        </div>
        <div className="space-y-3">
          {(overview?.modules || []).map((m) => {
            const chs = chaptersByModule(m.module_id);
            const isOpen = openModule === m.module_id;
            return (
              <div key={m.module_id} data-testid={`module-${m.module_id}`} className="bg-[#F3EFEA] border border-[#2C4A3E]/12 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenModule(isOpen ? null : m.module_id)}
                  data-testid={`module-toggle-${m.module_id}`}
                  className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-5 text-left"
                >
                  <div className="flex items-start gap-4">
                    <span className="w-9 h-9 shrink-0 rounded-full bg-[#2C4A3E] text-[#FAF8F5] flex items-center justify-center font-mono text-sm">{m.module_id}</span>
                    <div>
                      <h3 className="font-serif text-lg sm:text-xl text-[#1A2621]">{m.title}</h3>
                      <p className="text-sm text-[#5A6B61] mt-0.5">{m.summary}</p>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-[#2C4A3E] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <ul className="px-5 sm:px-6 pb-5 grid sm:grid-cols-2 gap-x-6 gap-y-1">
                    {chs.map((c) => (
                      <li key={c.chapter_number} className="flex items-center gap-2 py-1.5 text-sm text-[#334139] border-t border-[#2C4A3E]/8">
                        <Leaf className="w-3.5 h-3.5 text-[#829B88] shrink-0" />
                        <span className="font-mono text-xs text-[#829B88]">{String(c.chapter_number).padStart(2, "0")}</span>
                        {c.title}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="bg-[#2C4A3E] rounded-3xl px-6 sm:px-12 py-14 text-[#FAF8F5]">
          <h2 className="font-serif text-3xl sm:text-4xl">Begin your practice today</h2>
          <p className="mt-3 text-[#E8ECE8]/90 max-w-xl mx-auto">
            One payment of ${overview?.price_usd ?? 99} gives you a full year of access to all ninety
            chapters, every quiz, the final exam, and your certificate.
          </p>
          <button onClick={enroll} disabled={loadingEnroll} data-testid="enroll-cta-btn" className="mt-8 px-8 py-4 rounded-full bg-[#C69255] text-[#1A2621] font-semibold hover:bg-[#b9843f] transition-all hover:-translate-y-0.5 disabled:opacity-60">
            {user?.access_active || user?.role === "admin" ? "Go to Dashboard" : "Enroll Now"}
          </button>
        </div>
        <div className="mt-8"><DisclaimerBanner /></div>
      </section>
    </div>
  );
}
